import type { ReactElement } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'

export interface SwitchProps {
  checked: boolean
  onCheckedChange: (value: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false
}: SwitchProps): ReactElement {
  const generatedId = useId()
  const labelId = `switch-${generatedId}-label`
  const descriptionId = `switch-${generatedId}-description`

  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 ease-out-quart',
          checked ? 'bg-accent' : 'bg-ink-300 dark:bg-ink-700',
          disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'size-5 rounded-full bg-ink-50 shadow-sheet transition-transform duration-200 ease-out-quart',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
      {label || description ? (
        <div className="min-w-0">
          {label ? (
            <span id={labelId} className="block text-sm font-medium text-fg">
              {label}
            </span>
          ) : null}
          {description ? (
            <p id={descriptionId} className="mt-0.5 text-xs leading-relaxed text-fg-muted">
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
